import { useLazyQuery } from '@apollo/client';
import {
  GET_DASHBOARD_METRIC_DETAIL,
  MetricUnitType,
} from '@frontend/data-access';
import { Progress, Skeleton } from 'antd';
import { useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';

interface MetricStatsProps {
  channelId?: string;
  projectId?: string;
  challengeId?: string;
  topicId?: string;
  businessArea?: string;
  countryId?: string;
  metricId?: string;
}

function MetricStats({
  channelId,
  projectId,
  challengeId,
  topicId,
  businessArea,
  countryId,
  metricId,
}: MetricStatsProps) {
  const { t } = useTranslation('common');

  const [getData, { data, loading }] = useLazyQuery<{
    dashboardMetricDetail: {
      metric: string;
      type: MetricUnitType;
      ideas: number;
      target: number;
      result: number;
      label: string;
    }[];
  }>(GET_DASHBOARD_METRIC_DETAIL);

  const metric = metricId ? data?.dashboardMetricDetail[0] : null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  useEffect(() => {
    getData({
      variables: {
        channelId,
        projectId,
        challengeId,
        topicId,
        businessArea,
        countryId,
        metricId,
      },
    });
  }, [
    businessArea,
    challengeId,
    channelId,
    countryId,
    getData,
    projectId,
    topicId,
    metricId,
  ]);

  if (loading)
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-100 p-5 border border-gray-400 rounded-[20px]">
          <Skeleton active />
        </div>
        <div className="bg-gray-100 p-5 border border-gray-400 rounded-[20px]">
          <Skeleton active />
        </div>
        <div className="bg-gray-100 p-5 border border-gray-400 rounded-[20px]">
          <Skeleton active />
        </div>
      </div>
    );

  return (
    <div className="">
      {metric ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-100 p-5 border border-gray-400 rounded-[20px]">
            <div className="text-gray-900 font-medium mb-1">
              {t('total_target_amount')}
            </div>
            <div className="flex justify-between items-center">
              {metric.type === 'QUANTITATIVE' ? (
                <span className="text-xl font-semibold text-gray-900">
                  {formatCurrency(metric.target) + ' ' + metric.label}
                </span>
              ) : (
                <span className="text-xl font-semibold text-gray-900">-</span>
              )}
            </div>
          </div>
          <div className="bg-gray-100 p-5 border border-gray-400 rounded-[20px]">
            <div className="text-gray-900 font-medium mb-1">
              {t('total_actual_amount')}
            </div>
            <div className="flex justify-between items-center">
              {metric.type === 'QUANTITATIVE' ? (
                <span className="text-xl font-semibold text-gray-900">
                  {formatCurrency(metric.result) + ' ' + metric.label}
                </span>
              ) : (
                <span className="text-xl font-semibold text-gray-900">-</span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {data?.dashboardMetricDetail.map((metric, index) => (
            <div
              key={index}
              className="bg-gray-100 p-5 border border-gray-400 rounded-[20px]"
            >
              <div className="mb-4">
                <div className="text-gray-900 font-medium mb-1">
                  {metric.metric}
                </div>
                <div className="text-sm text-gray-600">
                  {t('ideas_count', { count: metric.ideas })}
                </div>
              </div>
              <div className="flex justify-between items-center">
                {metric.result ? (
                  <span className="text-lg font-semibold text-gray-900">
                    {formatCurrency(metric.target) + ' ' + metric.label}
                  </span>
                ) : (
                  <span className="text-sm text-gray-600">
                    {t('no_actual_amount')}
                  </span>
                )}
                <span className="text-sm text-gray-700">
                  {t('target_amount_value', {
                    target: formatCurrency(metric.target),
                    label: metric.label,
                  })}
                </span>
              </div>
              <Progress
                percent={(metric.result / metric.target) * 100}
                showInfo={false}
                strokeColor="#2563eb"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MetricStats;
